import { logger } from './logger';

interface WebSocketMessage {
  type: string;
  data: any;
  errorCode?: string;
  message?: string;
  retryable?: boolean;
  recommendedAction?: string;
}

export function isOidcTokenAuthError(errorCode?: string, message?: string): boolean {
  if (errorCode === 'INVALID_TOKEN') {
    return true;
  }
  if (errorCode === 'SESSION_INVALID' && /invalid token/i.test(message ?? '')) {
    return true;
  }
  return false;
}

interface WebSocketConfig {
  url: string;
  token: string;
  onMessage: (message: WebSocketMessage) => void;
  onError: (error: Event) => void;
  onClose: (event: CloseEvent) => void;
  onOpen: () => void;
  onConnectionStatusChange?: (status: 'connecting' | 'connected' | 'disconnected' | 'error') => void;
  /** Session access token rejected; refresh Aegis token (or remint) before full re-login. */
  onTokenInvalid?: (info: { code: string; message?: string }) => void;
  /** Aegis session invalid/expired; full re-login required. */
  onSessionInvalid?: (info: { code: string; message?: string }) => void;
}

export class WebSocketService {
  private ws: WebSocket | null = null;
  private config: WebSocketConfig;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3; // Reduced from 5 to 3
  private reconnectDelay = 5000; // Increased from 1000ms to 5000ms
  private isConnecting = false;
  private heartbeatInterval: number | null = null;
  private connectionTimeout: number | null = null;
  private suppressBrowserErrors = true; // Suppress browser error popups
  private connectionId = 0; // Track connection attempts to prevent conflicts
  private isDestroyed = false; // Prevent operations after destruction
  private lastConnectionAttempt = 0; // Track last connection attempt time
  private connectionCooldown = 10000; // 10 seconds cooldown between connection attempts
  private authSentAtMs: number | null = null;
  private authSucceeded = false;

  constructor(config: WebSocketConfig) {
    this.config = config;
    
    // Set up global error suppression for WebSocket errors
    if (this.suppressBrowserErrors) {
      this.setupErrorSuppression();
    }
  }

  connect(): void {
    if (this.isDestroyed) {
      logger.log(' [WebSocket] Service destroyed, ignoring connect request');
      return;
    }

    if (this.isConnecting) {
      logger.log(' [WebSocket] Connection already in progress, ignoring duplicate request');
      return;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      logger.log(' [WebSocket] Connection already established, ignoring duplicate request');
      return;
    }

    // Check cooldown to prevent rapid-fire connection attempts
    const now = Date.now();
    const timeSinceLastAttempt = now - this.lastConnectionAttempt;
    if (timeSinceLastAttempt < this.connectionCooldown) {
      logger.log(` [WebSocket] Connection cooldown active (${Math.round((this.connectionCooldown - timeSinceLastAttempt) / 1000)}s remaining)`);
      return;
    }

    if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
      logger.log(' [WebSocket] Connection already connecting, ignoring duplicate request');
      return;
    }

    // Increment connection ID to track this attempt
    const currentConnectionId = ++this.connectionId;
    this.isConnecting = true;
    this.cleanup();
    this.notifyStatusChange('connecting');

    try {
      const target = this.summarizeWsTarget(this.config.url);
      logger.log(
        ` [WebSocket] Connecting (attempt ${currentConnectionId})`,
        {
          target,
          hasToken: Boolean(this.config.token),
          tokenLength: this.config.token?.length ?? 0,
        }
      );
      this.lastConnectionAttempt = now;
      this.authSentAtMs = null;
      this.authSucceeded = false;
      this.ws = new WebSocket(this.config.url);

      // Set connection timeout
      this.connectionTimeout = window.setTimeout(() => {
        if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
          console.warn(' [WebSocket] Connection timeout');
          this.ws.close();
        }
      }, 10000);

      this.ws.onopen = () => {
        // Check if this connection is still valid (not superseded by a newer attempt)
        if (currentConnectionId !== this.connectionId) {
          logger.log(` [WebSocket] Connection ${currentConnectionId} superseded by newer attempt ${this.connectionId}, closing`);
          if (this.ws) {
            this.ws.close();
          }
          return;
        }

        logger.log(` [WebSocket] Connected (attempt ${currentConnectionId})`, {
          target: this.summarizeWsTarget(this.config.url),
          protocol: this.ws?.protocol || '(none)',
          extensions: this.ws?.extensions || '(none)',
        });
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.notifyStatusChange('connected');
        
        if (this.connectionTimeout) {
          clearTimeout(this.connectionTimeout);
          this.connectionTimeout = null;
        }

        // Send authentication message
        this.sendAuth();
        
        // Start heartbeat
        this.startHeartbeat();
        
        this.config.onOpen();
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          // Silently handle parse errors - these are expected for malformed messages
          console.warn(' [WebSocket] Error parsing message:', error);
        }
      };

      this.ws.onerror = (error) => {
        // Use console.warn to avoid Next.js error boundary interception
        // These are expected connection errors that should be handled gracefully
        console.warn(' [WebSocket] Connection error (suppressed from user):', {
          target: this.summarizeWsTarget(this.config.url),
          readyState: this.getReadyStateLabel(this.ws?.readyState),
          authSent: this.authSentAtMs != null,
          authSucceeded: this.authSucceeded,
          error,
        });
        this.isConnecting = false;
        this.notifyStatusChange('error');
        
        // Still call the error handler but catch any errors
        try {
          this.config.onError(error);
        } catch (err) {
          console.warn(' [WebSocket] Error in error handler callback:', err);
        }
        
        // Prevent browser error popups by stopping event propagation
        if (this.suppressBrowserErrors && error.preventDefault) {
          error.preventDefault();
        }
      };

      this.ws.onclose = (event) => {
        logger.log(' [WebSocket] Connection closed:', {
          target: this.summarizeWsTarget(this.config.url),
          code: event.code,
          reason: event.reason || '(empty)',
          wasClean: event.wasClean,
          readyStateAtClose: this.getReadyStateLabel(this.ws?.readyState),
          authSent: this.authSentAtMs != null,
          authSucceeded: this.authSucceeded,
          diagnostics: this.getCloseDiagnostics(event.code, this.authSucceeded),
        });
        this.isConnecting = false;
        this.cleanup();
        this.notifyStatusChange('disconnected');
        
        // Attempt reconnection if not a normal closure
        if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        }
        
        this.config.onClose(event);
      };

    } catch (error) {
      console.warn(' [WebSocket] Failed to create connection:', error);
      this.isConnecting = false;
      this.notifyStatusChange('error');
      try {
        this.config.onError(error as Event);
      } catch (err) {
        console.warn(' [WebSocket] Error in error handler callback:', err);
      }
    }
  }

  private sendAuth(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      // Send authentication message in the format expected by backend
      const authMessage = {
        type: 'auth',
        token: this.config.token
      };
      this.authSentAtMs = Date.now();
      logger.log(' [WebSocket] Sending authentication message', {
        tokenLength: this.config.token?.length ?? 0,
        target: this.summarizeWsTarget(this.config.url),
      });
      this.ws.send(JSON.stringify(authMessage));
    }
  }

  private handleMessage(message: any): void {
    try {
      // Safely check if message has required properties
      if (!message || typeof message !== 'object') {
        console.warn(' [WebSocket] Received invalid message format:', message);
        return;
      }

      // Skip logging and treat as no-op: server sent "error" because it doesn't recognize our pong response (benign)
      const errorData = String(message.data ?? message.message ?? '');
      if (message.type === 'error' && errorData.includes('Unknown message type: pong')) {
        return;
      }

      logger.log(' [WebSocket] Received message:', message);

      switch (message.type) {
        case 'connected':
          logger.log(' [WebSocket] Server confirmed connection');
          break;
        case 'auth_success':
          this.authSucceeded = true;
          logger.log(' [WebSocket] Authentication successful', {
            authRoundTripMs: this.authSentAtMs ? Date.now() - this.authSentAtMs : null,
          });
          break;
        case 'auth_error': {
          const m = message as WebSocketMessage;
          const nestedData =
            typeof m.data === 'object' && m.data !== null && !Array.isArray(m.data)
              ? (m.data as { errorCode?: string })
              : null;
          const errorCode = m.errorCode ?? nestedData?.errorCode;
          const detailMsg =
            typeof m.message === 'string'
              ? m.message
              : typeof m.data === 'string'
                ? m.data
                : '';

          if (errorCode === 'TRANSIENT_RETRYABLE') {
            console.warn(' [WebSocket] Transient auth error; reconnect path:', {
              target: this.summarizeWsTarget(this.config.url),
              authRoundTripMs: this.authSentAtMs ? Date.now() - this.authSentAtMs : null,
            });
            try {
              this.cleanup();
              if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                this.ws.close(4001, 'Transient auth error');
              }
            } catch (error) {
              console.warn(' [WebSocket] Error during transient auth close:', error);
            }
            break;
          }

          if (isOidcTokenAuthError(errorCode, detailMsg)) {
            try {
              this.config.onTokenInvalid?.({
                code: errorCode === 'INVALID_TOKEN' ? 'INVALID_TOKEN' : 'SESSION_INVALID',
                message: detailMsg || undefined,
              });
            } catch (err) {
              console.warn(' [WebSocket] onTokenInvalid callback error:', err);
            }
          } else {
            const sessionInvalidLike =
              errorCode === 'SESSION_INVALID' ||
              (!errorCode && /expired|not found/i.test(detailMsg));

            if (sessionInvalidLike) {
              try {
                this.config.onSessionInvalid?.({
                  code: errorCode === 'SESSION_INVALID' || !errorCode ? 'SESSION_INVALID' : String(errorCode),
                  message: detailMsg || undefined,
                });
              } catch (err) {
                console.warn(' [WebSocket] onSessionInvalid callback error:', err);
              }
            }
          }

          // Use console.warn for expected errors to avoid Next.js error boundary
          console.warn(' [WebSocket] Authentication failed:', {
            details: detailMsg || m.data,
            errorCode: errorCode ?? null,
            target: this.summarizeWsTarget(this.config.url),
            authRoundTripMs: this.authSentAtMs ? Date.now() - this.authSentAtMs : null,
          });
          try {
            this.disconnect();
          } catch (error) {
            console.warn(' [WebSocket] Error during disconnect:', error);
          }
          break;
        }
        case 'session_update':
          logger.log(' [WebSocket] Session update received');
          try {
            this.config.onMessage(message);
          } catch (error) {
            console.warn(' [WebSocket] Error in session_update handler:', error);
          }
          break;
        case 'tenant_update':
          logger.log(' [WebSocket] Tenant update received');
          try {
            this.config.onMessage(message);
          } catch (error) {
            console.warn(' [WebSocket] Error in tenant_update handler:', error);
          }
          break;
        case 'pong':
          // Heartbeat response received - silently handle (server may not recognize this)
          //logger.log(' [WebSocket] Heartbeat response received');
          break;
        case 'ping':
          // Server sent ping - respond with ping so backend returns `pong`
          // The backend `SessionWebSocketHandler` expects a `ping` message back (it responds with `pong`),
          // so avoid sending `{ type: 'pong' }` which would be treated as "Unknown message type: pong".
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            try {
              this.ws.send(JSON.stringify({ type: 'ping' }));
            } catch (error) {
              // Silently handle send errors for pong responses
              console.warn(' [WebSocket] Error sending ping response:', error);
            }
          }
          break;
        case 'error':
          // Use console.warn for expected server errors to avoid Next.js error boundary
          // Filter out "Unknown message type: pong" errors as they're expected when server doesn't support pong
          const errorMessage = message.data || message.message || '';
          if (errorMessage.includes('Unknown message type: pong')) {
            // Silently ignore - server doesn't recognize pong responses, which is fine
            logger.log(' [WebSocket] Server does not recognize pong messages (expected)');
          } else {
            console.warn(' [WebSocket] Server error:', errorMessage);
          }
          break;
        default:
          logger.log(' [WebSocket] Unknown message type:', message.type);
          try {
            this.config.onMessage(message);
          } catch (error) {
            console.warn(' [WebSocket] Error in default message handler:', error);
          }
      }
    } catch (error) {
      // Catch any unexpected errors in message handling
      console.warn(' [WebSocket] Unexpected error in handleMessage:', error);
    }
  }

  private startHeartbeat(): void {
    this.heartbeatInterval = window.setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // Send ping message in the format expected by backend
        const pingMessage = { type: 'ping' };
        this.ws.send(JSON.stringify(pingMessage));
      }
    }, 30000); // 30 seconds
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    logger.log(` [WebSocket] Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`, {
      target: this.summarizeWsTarget(this.config.url),
      lastCloseAuthSucceeded: this.authSucceeded,
    });
    
    setTimeout(() => {
      this.connect();
    }, delay);
  }

  private cleanup(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
  }

  private notifyStatusChange(status: 'connecting' | 'connected' | 'disconnected' | 'error'): void {
    if (this.config.onConnectionStatusChange) {
      this.config.onConnectionStatusChange(status);
    }
  }

  private setupErrorSuppression(): void {
    // Override the global error handler to suppress WebSocket connection errors
    const originalErrorHandler = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      // Check if this is a WebSocket connection error
      if (typeof message === 'string' && 
          (message.includes('WebSocket') || 
           message.includes('ws://') || 
           message.includes('NS_ERROR_WEBSOCKET_CONNECTION_REFUSED'))) {
        logger.log(' [WebSocket] Suppressed browser error popup:', message);
        return true; // Prevent default error handling
      }
      
      // For all other errors, use the original handler
      if (originalErrorHandler) {
        return originalErrorHandler(message, source, lineno, colno, error);
      }
      return false;
    };
  }

  send(message: WebSocketMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn(' [WebSocket] Cannot send message - connection not open');
    }
  }

  disconnect(): void {
    logger.log(' [WebSocket] Disconnecting');
    this.isDestroyed = true;
    this.cleanup();
    
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    
    this.isConnecting = false;
    this.notifyStatusChange('disconnected');
  }

  /**
   * After session access token refresh (or remint), reconnect with the new token without a full page reload.
   */
  reconnectWithToken(newToken: string): void {
    logger.log(' [WebSocket] Reconnecting with refreshed session access token');
    this.config = { ...this.config, token: newToken };
    this.isDestroyed = false;
    this.authSucceeded = false;
    this.authSentAtMs = null;
    this.reconnectAttempts = 0;
    if (this.ws) {
      try {
        this.ws.close(1000, 'Token refresh reconnect');
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
    this.cleanup();
    this.isConnecting = false;
    this.connect();
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  isConnectingOrConnected(): boolean {
    return this.isConnecting || (this.ws !== null && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN));
  }

  canConnect(): boolean {
    return !this.isDestroyed && !this.isConnectingOrConnected();
  }

  private summarizeWsTarget(rawUrl: string): string {
    try {
      const parsed = new URL(rawUrl);
      return `${parsed.protocol}//${parsed.host}${parsed.pathname}`;
    } catch {
      return rawUrl;
    }
  }

  private getReadyStateLabel(state?: number): string {
    switch (state) {
      case WebSocket.CONNECTING:
        return 'CONNECTING';
      case WebSocket.OPEN:
        return 'OPEN';
      case WebSocket.CLOSING:
        return 'CLOSING';
      case WebSocket.CLOSED:
        return 'CLOSED';
      default:
        return 'UNKNOWN';
    }
  }

  private getCloseDiagnostics(code: number, authSucceeded: boolean): string {
    switch (code) {
      case 1000:
        return 'Normal closure (client/server initiated).';
      case 1001:
        return 'Endpoint going away (browser navigation/server restart).';
      case 1002:
        return 'Protocol error (proxy/server websocket protocol mismatch).';
      case 1003:
        return 'Unsupported data type from peer.';
      case 1005:
        return 'No close code present in frame.';
      case 1006:
        return authSucceeded
          ? 'Abnormal closure after auth; likely network/proxy drop or upstream timeout.'
          : 'Abnormal closure before auth success; likely handshake/auth rejection or proxy/network interruption.';
      case 1007:
      case 1008:
        return 'Policy violation; often auth/authorization rejection.';
      case 1009:
        return 'Message too big for server policy.';
      case 1011:
        return 'Server internal error while handling websocket.';
      case 1012:
      case 1013:
        return 'Server restart/overload; retry recommended.';
      case 1015:
        return 'TLS handshake failure (certificate/termination issue).';
      default:
        return 'Unclassified close code; inspect upstream proxy/server websocket logs.';
    }
  }
}
